import {
  cohortsForGroupDropdown,
  VISIT_TYPE,
  isAdhocVisit,
  visitDraftFromApiRow,
  visitsForVisitRail,
} from "./scheduleVisitDraft";
import {
  COMM_TEMPLATE_LABELS,
  COMM_TEMPLATE_TYPES,
  applyOrgChannelAvailability,
  fetchAdhocSetup,
  fetchEcommTemplates,
  fetchOrganisationChannelFlags,
  getSendableCommChannels,
  mergeCommTemplates,
} from "./scheduleOptionalSetupService";

export function getFirstAppointmentAnchor() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1, 9, 0, 0, 0);
}

export function parseDayOffset(value) {
  const parsed = parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function formatAppointmentLabel(date) {
  return date.toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function visitTimelineKey(visit) {
  if (visit?.visitsPerStudyId) return String(visit.visitsPerStudyId);
  if (visit?.id) return String(visit.id);
  return null;
}

function findReferenceVisit(allVisits, refId) {
  const trimmed = String(refId || "").trim();
  if (!trimmed) return null;
  return (
    allVisits.find((row) => String(row.visitsPerStudyId) === trimmed) ||
    allVisits.find((row) => String(row.id) === trimmed) ||
    null
  );
}

export function projectVisitAppointmentDate(visit, visitDateMap, allVisits, anchorDate) {
  if (!visit) return null;

  const key = visitTimelineKey(visit);
  if (key && visitDateMap.has(key)) {
    return visitDateMap.get(key);
  }

  const isScreening = visit.visitType === VISIT_TYPE.SCREENING || visit.isScreening;
  let baseDate = anchorDate;
  let dayOffset = 0;

  if (isScreening) {
    dayOffset = parseDayOffset(visit.screeningWindowStart);
  } else {
    const refVisit = findReferenceVisit(allVisits, visit.refVisitId);
    if (refVisit) {
      baseDate =
        projectVisitAppointmentDate(refVisit, visitDateMap, allVisits, anchorDate) ||
        anchorDate;
    }
    dayOffset =
      visit.visitType === VISIT_TYPE.RESIDENTIAL
        ? parseDayOffset(visit.startPeriod)
        : parseDayOffset(visit.nonResidVisitOccur);
  }

  const appointmentDate = addDays(baseDate, dayOffset);
  if (key) {
    visitDateMap.set(key, appointmentDate);
  }
  return appointmentDate;
}

export function resolveTimelineVisits({ wizard, visitBuilderMode, visitCohorts, cohort = null }) {
  if (visitBuilderMode === "cohorts" && visitCohorts?.length) {
    const dropdownCohorts = cohortsForGroupDropdown(visitCohorts);
    const active =
      dropdownCohorts.find(
        (cohortRow) => !cohortRow.isStudyScreeningGroup && !cohortRow.isSharedSchedule,
      ) ||
      dropdownCohorts.find((cohortRow) => !cohortRow.isStudyScreeningGroup) ||
      dropdownCohorts[0];
    const cohortVisits = visitsForVisitRail(active?.visits || [], { includeAdhoc: true });
    if (cohortVisits.length) return cohortVisits;
  }

  const draftVisits = visitsForVisitRail(wizard?.visitDrafts || [], { includeAdhoc: true });
  if (draftVisits.length) return draftVisits;

  const apiRows = Array.isArray(cohort?.visits) ? cohort.visits : [];
  if (apiRows.length) {
    return visitsForVisitRail(
      apiRows.map((row, index) => visitDraftFromApiRow(row, index)),
      { includeAdhoc: true },
    );
  }

  return [];
}

function resolveTimelineGroupId({ wizard, visitBuilderMode, visitCohorts }) {
  if (visitBuilderMode === "cohorts" && visitCohorts?.length) {
    const dropdownCohorts = cohortsForGroupDropdown(visitCohorts);
    const active =
      dropdownCohorts.find(
        (cohort) => !cohort.isStudyScreeningGroup && !cohort.isSharedSchedule,
      ) ||
      dropdownCohorts.find((cohort) => !cohort.isStudyScreeningGroup) ||
      dropdownCohorts[0];
    return active?.id || null;
  }
  const groupNames = wizard?.groupNames || [];
  const first = groupNames[0];
  return first?.GroupNameId || first?.groupNameId || first?.GroupNameIdRef || first?.groupNameIdRef || null;
}

function mapTimelineCommunications(templates = [], orgChannels = {}) {
  const complete = mergeCommTemplates(templates);
  const gated = applyOrgChannelAvailability(complete, orgChannels);
  return COMM_TEMPLATE_TYPES.map((type) => {
    const template = gated.find((row) => row.TemplateType === type) || {
      TemplateType: type,
    };
    return {
      type,
      label: COMM_TEMPLATE_LABELS[type] || type,
      channels: getSendableCommChannels(template),
    };
  });
}

async function fetchVisitCommunications({
  visit,
  studyScheduleId,
  organizationId,
  createdBy,
  groupId,
  eCommunicationEnabled,
  orgChannels,
}) {
  if (!eCommunicationEnabled) return [];
  const adhoc = isAdhocVisit(visit);
  if (!adhoc && !visit?.visitsPerStudyId) return [];

  try {
    const data = adhoc
      ? await fetchAdhocSetup({
          studyScheduleId,
          organizationId,
          createdBy,
          groupId,
        })
      : await fetchEcommTemplates({
          studyScheduleId,
          visitId: visit.visitsPerStudyId,
          organizationId,
          createdBy,
          groupId,
        });
    return mapTimelineCommunications(data?.templates || [], orgChannels);
  } catch {
    return mapTimelineCommunications([], orgChannels);
  }
}

export async function buildVisitAppointmentTimeline({
  visits = [],
  setupFlags = {},
  studyScheduleId,
  organizationId,
  createdBy,
  groupId = null,
}) {
  const anchorDate = getFirstAppointmentAnchor();
  const anchorLabel = formatAppointmentLabel(anchorDate);
  const visitDateMap = new Map();
  const eCommunicationEnabled = !!setupFlags?.eCommunication;

  const projected = visits.map((visit) => {
    if (isAdhocVisit(visit)) {
      return {
        visit,
        visitName: visit.visitName || "Adhoc Visit",
        visitId: visit.visitsPerStudyId || "adhoc",
        isAdhoc: true,
        appointmentDate: null,
        appointmentLabel: "When booked",
        dayOffset: 0,
        communications: [],
      };
    }

    const appointmentDate = projectVisitAppointmentDate(
      visit,
      visitDateMap,
      visits,
      anchorDate,
    );
    const dayOffset = isScreeningVisit(visit)
      ? parseDayOffset(visit.screeningWindowStart)
      : visit.visitType === VISIT_TYPE.RESIDENTIAL
        ? parseDayOffset(visit.startPeriod)
        : parseDayOffset(visit.nonResidVisitOccur);

    return {
      visit,
      visitName: visit.visitName || "Visit",
      visitId: visit.visitsPerStudyId || null,
      isAdhoc: false,
      appointmentDate,
      appointmentLabel: appointmentDate ? formatAppointmentLabel(appointmentDate) : "",
      dayOffset,
      communications: [],
    };
  });

  if (studyScheduleId && organizationId && eCommunicationEnabled) {
    const orgChannels = await fetchOrganisationChannelFlags(organizationId);
    await Promise.all(
      projected.map(async (entry) => {
        entry.communications = await fetchVisitCommunications({
          visit: entry.visit,
          studyScheduleId,
          organizationId,
          createdBy,
          groupId,
          eCommunicationEnabled,
          orgChannels,
        });
      }),
    );
  }

  return {
    anchorDate,
    anchorLabel,
    visits: projected.map(({ visit, ...rest }) => rest),
  };
}

function isScreeningVisit(visit) {
  return visit?.visitType === VISIT_TYPE.SCREENING || visit?.isScreening;
}

export async function buildTimelineFromScheduleContext({
  wizard,
  visitBuilderMode,
  visitCohorts,
  cohort = null,
  setupFlags,
  studyScheduleId,
  organizationId,
  createdBy,
}) {
  const visits = resolveTimelineVisits({ wizard, visitBuilderMode, visitCohorts, cohort });
  const groupId = resolveTimelineGroupId({ wizard, visitBuilderMode, visitCohorts });

  if (!visits.length) {
    const anchorDate = getFirstAppointmentAnchor();
    return {
      anchorDate,
      anchorLabel: formatAppointmentLabel(anchorDate),
      visits: [],
    };
  }

  return buildVisitAppointmentTimeline({
    visits,
    setupFlags,
    studyScheduleId,
    organizationId,
    createdBy,
    groupId,
  });
}
